/**
 * The one logging surface.
 *
 * Never log a credential, a cookie, a password body, or PII beyond an id (best practices §5.5).
 * In production only warnings and errors survive — a console full of debug chatter is where a
 * real error goes to hide.
 */

const isDev = import.meta.env.DEV

type Fields = Record<string, unknown>

function emit(level: 'debug' | 'warn' | 'error', message: string, fields?: Fields): void {
  if (level === 'debug' && !isDev) return

  const payload = fields ? [message, fields] : [message]

  if (level === 'error') {
    console.error('[vecos]', ...payload)
  } else if (level === 'warn') {
    console.warn('[vecos]', ...payload)
  } else if (isDev) {
    console.warn('[vecos:debug]', ...payload)
  }
}

export const log = {
  debug: (message: string, fields?: Fields) => { emit('debug', message, fields) },
  warn: (message: string, fields?: Fields) => { emit('warn', message, fields) },
  error: (message: string, fields?: Fields) => { emit('error', message, fields) },

  /** For a caught value of unknown type, which is what a `catch` actually gives you. */
  exception: (message: string, cause: unknown, fields?: Fields) => {
    emit('error', message, {
      ...fields,
      error: cause instanceof Error ? { name: cause.name, message: cause.message } : String(cause),
    })
  },
}
